import React, { useCallback, useEffect, useState } from 'react';
import { Menu } from 'antd';
import PropTypes from 'prop-types';

import { ReactComponent as CalculatorIcon } from '../../../../assets/icons/calculator.svg';
import { ReactComponent as ChartIcon } from '../../../../assets/icons/chart.svg';
import { ReactComponent as NotepadIcon } from '../../../../assets/icons/notepad.svg';

import Permissions from '../../../../auth/Permissions';

const SECTIONS = [
  { key: 'overview', label: 'Overview', Icon: ChartIcon },
  {
    key: 'budget-breakdown',
    label: 'Budget Breakdown',
    Icon: CalculatorIcon,
    children: [
      { key: 'budget', label: 'Budget & Actual Costs' },
      { key: 'sub-contracts', label: 'Sub-Contracts' },
      { key: 'uncategorized-costs', label: 'Uncategorized Costs' },
    ],
  },
  { key: 'estimates', label: 'Estimates', Icon: NotepadIcon },
];

const SUB_SECTION_KEYS = ['budget', 'sub-contracts', 'uncategorized-costs'];

export const ALL_SECTION_KEYS = [
  'overview',
  'budget-breakdown',
  ...SUB_SECTION_KEYS,
  'estimates',
];

export default function CostTrackingNavigation({
  hasUncategorizedSection,
}) {
  const hasPOPerms = Permissions.match('PURCHASE_ORDERS');
  const [activeKey, setActiveKey] = useState(ALL_SECTION_KEYS[0]);

  const handleMenuClick = useCallback((key) => {
    setActiveKey(key);
    document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveKey(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );
    ALL_SECTION_KEYS.forEach((key) => {
      const el = document.getElementById(key);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const selectedKeysForMenu = SUB_SECTION_KEYS.includes(activeKey)
    ? ['budget-breakdown']
    : [activeKey];

  return (
    <Menu
      mode="inline"
      selectedKeys={selectedKeysForMenu}
      openKeys={['budget-breakdown']}
      onOpenChange={() => {}}
      onClick={({ key }) => handleMenuClick(key)}
      className="cost-tracking-nav"
    >
      {SECTIONS.map((section) => {
        if (section.children) {
          return (
            <Menu.SubMenu
              key={section.key}
              className={selectedKeysForMenu.includes(section.key) ? 'cost-tracking-submenu-selected' : ''}
              title={(
                <span
                  className="cost-tracking-nav-item"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClick(section.key);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMenuClick(section.key);
                    }
                  }}
                >
                  <section.Icon className="cost-tracking-nav-icon" />
                  <span>{section.label}</span>
                </span>
              )}
            >
              {section.children.map(({ key, label }) => {
                if (
                  (key === 'sub-contracts' && !hasPOPerms)
                  || (key === 'uncategorized-costs' && !hasUncategorizedSection)
                ) return null;
                return <Menu.Item key={key}>{label}</Menu.Item>;
              })}
            </Menu.SubMenu>
          );
        }

        return (
          <Menu.Item key={section.key}>
            <span className="cost-tracking-nav-item">
              <section.Icon className="cost-tracking-nav-icon" />
              <span>{section.label}</span>
            </span>
          </Menu.Item>
        );
      })}
    </Menu>
  );
}

CostTrackingNavigation.propTypes = {
  hasUncategorizedSection: PropTypes.bool.isRequired,
};
